import React, { forwardRef, useCallback, useId } from 'react';
import { InputGroup, AutoComplete } from 'rsuite';
import FormFieldErrorIcon from './FormFieldErrorIcon';
import IconIndicator from '../user-feedback/IconIndicator';
import { AddressOption } from 'src/types/address';

export interface AutocompleteWithErrorStatusProps {
  autoComplete?: string;
  data: Array<AddressOption>;
  disabled?: boolean;
  errorMessage: string;
  hideErrorIcon?: boolean;
  loading?: boolean;
  name?: string;
  onChange: (value: string) => void;
  onSelect?: (value?: AddressOption) => void;
  showError?: boolean;
  value?: AddressOption | string;
}

export const AutocompleteWithErrorStatus = React.memo(
  forwardRef<HTMLDivElement, AutocompleteWithErrorStatusProps>(
    (
      {
        errorMessage,
        hideErrorIcon,
        loading,
        showError,
        onSelect,
        value,
        ...autoCompleteProps
      },
      ref
    ) => {
      const inputId = useId();
      const listboxId = `${inputId}-listbox`;

      const handleOpen = useCallback(() => {
        requestAnimationFrame(() => {
          const listbox = document.getElementById(listboxId);
          if (listbox) {
            listbox.setAttribute(
              'aria-label',
              `${autoCompleteProps.name ?? ''} suggestions`
            );
            listbox.setAttribute('tabindex', '0');
          }
        });
      }, [listboxId, autoCompleteProps.name]);

      const errorId =
        showError && autoCompleteProps.name
          ? `${autoCompleteProps.name}-error`
          : undefined;

      return (
        <>
          <InputGroup
            role="group"
            ref={ref}
          >
            <AutoComplete
              {...autoCompleteProps}
              id={inputId}
              aria-controls={listboxId}
              aria-label={autoCompleteProps.name}
              aria-invalid={showError || undefined}
              aria-describedby={errorId}
              autoComplete={autoCompleteProps.autoComplete ?? 'off'}
              onOpen={handleOpen}
              onSelect={(selected) => {
                const match = autoCompleteProps.data.find(
                  (add) => add.value === selected
                );
                if (onSelect)
                  onSelect(match ?? ({ value: selected } as AddressOption));
              }}
              value={typeof value === 'object' ? value?.value : value}
            />
            {!hideErrorIcon && showError && (
              <InputGroup.Addon
                tabIndex={-1}
                data-testid="error-addon"
              >
                <FormFieldErrorIcon errorMessage={errorMessage} />
              </InputGroup.Addon>
            )}
            {loading && (
              <InputGroup.Addon
                tabIndex={-1}
                style={{ backgroundColor: 'transparent' }}
              >
                <IconIndicator
                  size={16}
                  color="#D1D1D1"
                />
              </InputGroup.Addon>
            )}
          </InputGroup>
          {showError && errorMessage && (
            <span
              id={`${autoCompleteProps.name}-error`}
              className="text-error"
            >
              {errorMessage}
            </span>
          )}
        </>
      );
    }
  )
);
